"use client";
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";
import {
  AtAlert,
  AtAlertVariant,
  AtAlertProps,
} from "@csc-dashboard/components-library";

export interface AlertOptions {
  duration?: number;
  action?: () => void;
  buttonText?: string;
  showCloseButton?: boolean;
  autoClose?: boolean;
  buttonClassName?: string;
  backdropClose?: boolean;
}

export interface AlertContextType {
  showError: (message: React.ReactNode, options?: AlertOptions) => void;
  showSuccess: (message: React.ReactNode, options?: AlertOptions) => void;
  showWarning: (message: React.ReactNode, options?: AlertOptions) => void;
  showInfo: (message: React.ReactNode, options?: AlertOptions) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

interface AlertProviderProps {
  children: ReactNode;
}

// eslint-disable-next-line react/prop-types
export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [currentAlert, setCurrentAlert] = useState<AtAlertProps | undefined>();

  const showAlert = useCallback(
    (
      variant: AtAlertVariant,
      message: React.ReactNode,
      options?: AlertOptions,
    ) => {
      const {
        duration = 5000,
        action,
        buttonText,
        showCloseButton,
        autoClose = true,
        buttonClassName,
        backdropClose,
      } = options || {};

      // Close current alert first if it exists
      if (currentAlert?.open) {
        setCurrentAlert(undefined);
      }

      // Set new alert after a brief delay to ensure smooth transition
      setTimeout(() => {
        setCurrentAlert({
          open: true,
          alertText: message,
          variant,
          timeOut: duration,
          setClose: () => setCurrentAlert(undefined),
          action,
          buttonText,
          showCloseButton,
          autoClose,
          buttonClassName,
          backdropClose,
        });
      }, 100);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally empty to keep callback stable and avoid infinite loops
    [],
  );

  const showError = useCallback(
    (message: React.ReactNode, options?: AlertOptions) => {
      showAlert(AtAlertVariant.ERROR, message, options);
    },
    [showAlert],
  );

  const showSuccess = useCallback(
    (message: React.ReactNode, options?: AlertOptions) => {
      showAlert(AtAlertVariant.SUCCESS, message, options);
    },
    [showAlert],
  );

  const showWarning = useCallback(
    (message: React.ReactNode, options?: AlertOptions) => {
      showAlert(AtAlertVariant.WARNING, message, options);
    },
    [showAlert],
  );

  const showInfo = useCallback(
    (message: React.ReactNode, options?: AlertOptions) => {
      showAlert(AtAlertVariant.INFO, message, options);
    },
    [showAlert],
  );

  const value: AlertContextType = {
    showError,
    showSuccess,
    showWarning,
    showInfo,
  };

  return (
    <AlertContext.Provider value={value}>
      {children}
      {currentAlert && (
        <AtAlert
          showCloseButton={currentAlert.showCloseButton}
          buttonText={currentAlert.buttonText}
          buttonClassName={currentAlert.buttonClassName}
          action={currentAlert.action}
          open={currentAlert.open}
          alertText={currentAlert.alertText}
          variant={currentAlert.variant}
          setClose={currentAlert.setClose}
          timeOut={currentAlert.timeOut}
          autoClose={currentAlert.autoClose}
          backdropClose={currentAlert.backdropClose}
        />
      )}
    </AlertContext.Provider>
  );
};

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
};
